import { isUserActive } from '@/lib/presence/userActivity'
import { getNotificationPrefs } from './prefs'

/**
 * Os três sinais que decidem entre toast e bandeja do SO. Separados do
 * `deveNotificarSistemaAgora` para que a REGRA seja testável sem browser e sem
 * relógio.
 */
export interface SinaisDeAtencao {
  /** `document.hasFocus()` — é a janela em que a pessoa está, não só uma janela visível. */
  temFoco: boolean
  /** Houve atividade real dentro do prazo de idle. */
  ativo: boolean
  /** O usuário pediu notificação do SO mesmo com o app na frente. */
  prefSempre: boolean
}

/**
 * Vale a pena mandar isto para a bandeja do sistema operacional?
 *
 * | situação | resposta |
 * |---|---|
 * | janela em foco **e** usuário ativo | `false` — só toast |
 * | janela visível, sem foco | `true` |
 * | aba escondida / minimizada / outro app | `true` |
 * | janela em foco mas parado há 5+ min | `true` |
 *
 * ## Por que `hasFocus` e não `document.hidden`
 *
 * `hidden` é grosso demais: uma aba aberta num segundo monitor conta como
 * "visível", e com esse divisor o aviso vira um toast que nasce e morre sem
 * ninguém ver. `hasFocus()` é o único primitivo que distingue "está na tela" de
 * "é a janela em que a pessoa está" — e é exatamente essa a pergunta.
 *
 * ## Por que atividade, e por que ESTA atividade
 *
 * Foco sozinho mente na direção oposta: uma janela deixada aberta na frente
 * enquanto a pessoa sai para o café tem foco e não tem ninguém. O sinal de
 * atividade vem de [[../presence/userActivity]], que é o MESMO que a presença
 * usa para marcar `afk` — não um segundo conceito de "estar aí".
 *
 * Mouse (`mouseenter`, posição do ponteiro) foi descartado de propósito: não
 * existe em toque, e mouse parado não é ausência. Quem lê uma conversa longa
 * fica minutos sem mexer, e silenciar justamente quem está mais atento seria o
 * inverso do que a regra quer.
 *
 * ## O que esta função NÃO decide
 *
 * Se o toast é adequado. Isso é do chamador — e é por isso que a resposta é um
 * booleano de "tentar o SO", não uma escolha de superfície. O `useNotification`
 * ainda cala o toast com a aba escondida (anti-duplicação com o
 * `useChannelActivityToast`), e o inbox nunca toasta `critical`. Essas regras
 * são de cada produtor; esta aqui é a comum aos três.
 */
export function deveNotificarSistema({ temFoco, ativo, prefSempre }: SinaisDeAtencao): boolean {
  if (prefSempre) return true
  return !(temFoco && ativo)
}

/**
 * A mesma regra, lendo os sinais do ambiente. **É esta que os produtores
 * chamam** — chat ([[../../hooks/useNotification]]), canal
 * ([[../../hooks/useChannelActivityToast]]) e inbox
 * ([[../../hooks/notifications/useBrowserNotifications]]) —, sempre pela porta
 * de [[notify]].
 *
 * Cada produtor lendo `hasFocus()` e a pref por conta própria seria a "segunda
 * realidade" que a regra existe para não ter: bastaria um deles esquecer de
 * consultar a pref, ou usar `hidden` no lugar de `hasFocus`, para dois avisos do
 * mesmo app discordarem sobre onde aparecer.
 *
 * Fora do browser devolve `true`: sem `document` não há toast, então não há
 * alternativa a preservar.
 */
export function deveNotificarSistemaAgora(): boolean {
  return deveNotificarSistema({
    temFoco: typeof document !== 'undefined' && document.hasFocus(),
    ativo: isUserActive(),
    prefSempre: getNotificationPrefs().systemWhenFocused,
  })
}
